import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../../../components/common/Button';
import { agentApi } from '../../../services/agentApi';
import { AgentAuditListItem, AgentAuditRow } from '../../../types/interfaces';
import { formatDateTime } from '../../../utils/dates';
import DownloadButton from './DownloadButton';
import { errorDetail } from './AgentSettings';
import './agent.css';

export const AUDIT_PAGE_SIZE = 50;

/** One-line summary of an audit row's counts: table changes, API calls, MinIO and outputs. */
export function auditSummary(counts: Record<string, unknown> | null): string {
  if (!counts) return '';
  const parts: string[] = [];
  const tables = (counts.tables || {}) as Record<string, Record<string, number>>;
  Object.entries(tables).forEach(([table, ops]) => {
    const changed = ['insert', 'update', 'delete'].filter((op) => (ops?.[op] || 0) > 0)
      .map((op) => `${ops[op]} ${op}`);
    if (changed.length) parts.push(`${table}: ${changed.join(', ')}`);
  });
  const extra: [string, string][] = [
    ['api_calls', 'API calls'], ['minio_put', 'files stored'], ['minio_deleted', 'files deleted'], ['outputs', 'outputs'],
  ];
  extra.forEach(([key, label]) => {
    const n = Number(counts[key] || 0);
    if (n > 0) parts.push(`${n} ${label}`);
  });
  return parts.join(' · ');
}

function resultClass(result: string | null): string {
  if (result === 'done') return 'agent-tag agent-tag--done';
  if (result === 'unknown') return 'agent-tag agent-tag--unknown';
  if (result === 'failed') return 'agent-tag agent-tag--failed';
  return 'agent-tag agent-tag--read';
}

function formatSize(size: number | null): string {
  if (size === null || size === undefined) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

const AuditDetail: React.FC<{ row: AgentAuditRow }> = ({ row }) => (
  <div className="agent-msg--bot agent-audit-detail">
    <div className="agent-tags">
      <span className={resultClass(row.result)}>{row.result || 'pending'}</span>
      <span className="agent-meta">
        Audit {row.id} · plan {row.plan_id ?? '-'} · conversation {row.conversation_id ?? '-'}
      </span>
    </div>
    {row.plan?.plan?.summary && <p className="agent-plan__summary">{row.plan.plan.summary}</p>}
    <p className="agent-meta">
      Requested by {row.requested_by || '-'} · approved by {row.approved_by || '-'} ·
      started {formatDateTime(row.started_at)} · finished {formatDateTime(row.finished_at)}
    </p>
    {row.error && <div className="agent-banner agent-banner--error">{row.error}</div>}
    {auditSummary(row.counts) && <p>{auditSummary(row.counts)}</p>}
    {row.steps_run && row.steps_run.length > 0 && (
      <ol className="agent-plan__steps">
        {row.steps_run.map((s) => (
          <li key={s.id}>
            <code>{s.kind}</code> {s.skipped ? 'skipped' : s.ok ? 'ok' : 'failed'}
            {s.detail && <span className="agent-meta"> · {s.detail}</span>}
          </li>
        ))}
      </ol>
    )}
    {row.links.length > 0 && (
      <div>
        {row.links.map((link, i) => (
          <div className="agent-dl" key={`${link.file_name}-${i}`}>
            <span>{link.file_name}</span>
            <span className="agent-meta">{link.kind} {formatSize(link.size)}</span>
            {link.url
              ? <DownloadButton url={link.url}>Download</DownloadButton>
              : <span className="agent-meta">link unavailable</span>}
          </div>
        ))}
        <p className="agent-meta">Links expire 24 h after this entry was opened.</p>
      </div>
    )}
  </div>
);

/** Executed plans, newest first; a row opens its details and file links. */
const AgentAudit: React.FC = () => {
  const [items, setItems] = useState<AgentAuditListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<AgentAuditRow | null>(null);
  const [detailError, setDetailError] = useState('');
  const mounted = useRef(true);
  const openRef = useRef<number | null>(null); // the row whose detail may still be applied

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const page = await agentApi.listAudit({ limit: AUDIT_PAGE_SIZE, offset, q: search || undefined });
      if (!mounted.current) return;
      setItems(page.items);
      setTotal(page.total);
      setError('');
    } catch (err) {
      if (mounted.current) setError(errorDetail(err, 'Could not load the audit log'));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [offset, search]);

  useEffect(() => { load(); }, [load]);

  const open = async (id: number) => {
    if (openId === id) {
      openRef.current = null;
      setOpenId(null);
      return;
    }
    openRef.current = id;
    setOpenId(id);
    setDetail(null);
    setDetailError('');
    try {
      const row = await agentApi.getAudit(id);
      if (mounted.current && openRef.current === id) setDetail(row);
    } catch (err) {
      // A late answer for a row that is no longer open is dropped.
      if (mounted.current && openRef.current === id) setDetailError(errorDetail(err, 'Could not load the audit entry'));
    }
  };

  const pages = Math.max(1, Math.ceil(total / AUDIT_PAGE_SIZE));
  const page = Math.floor(offset / AUDIT_PAGE_SIZE) + 1;

  return (
    <div className="agent-panel">
      <form className="agent-header-row" role="search"
        onSubmit={(e) => { e.preventDefault(); setOffset(0); setSearch(query.trim()); }}>
        <input className="input input--default input--sm" aria-label="Search the audit log" maxLength={200}
          placeholder="User, result, or audit/plan/conversation id" value={query} onChange={(e) => setQuery(e.target.value)} />
        <Button variant="secondary" size="sm" type="submit">Search</Button>
      </form>
      {error && <div className="agent-inline-error" role="alert">{error}</div>}
      <div className="agent-table-wrap">
        <table className="agent-table">
          <thead>
            <tr><th>Time</th><th>Requested by</th><th>Approved by</th><th>Summary</th><th>Result</th></tr>
          </thead>
          <tbody>
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="agent-meta">No executed plans yet.</td></tr>
            )}
            {items.map((item) => (
              <React.Fragment key={item.id}>
                <tr>
                  <td>
                    <button type="button" className="agent-link-button" aria-expanded={openId === item.id}
                      onClick={() => open(item.id)}>
                      {formatDateTime(item.finished_at || item.started_at || item.created_at)}
                    </button>
                  </td>
                  <td>{item.requested_by || '-'}</td>
                  <td>{item.approved_by || '-'}</td>
                  <td>{auditSummary(item.counts) || item.error || '-'}</td>
                  <td><span className={resultClass(item.result)}>{item.result || 'pending'}</span></td>
                </tr>
                {openId === item.id && (
                  <tr>
                    <td colSpan={5}>
                      {detailError && <div className="agent-inline-error" role="alert">{detailError}</div>}
                      {!detail && !detailError && <span className="agent-meta">Loading…</span>}
                      {detail && detail.id === item.id && <AuditDetail row={detail} />}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="agent-plan__buttons">
        <Button variant="outline" size="sm" disabled={loading || offset === 0}
          onClick={() => setOffset(Math.max(0, offset - AUDIT_PAGE_SIZE))}>Previous</Button>
        <span className="agent-meta">Page {page} of {pages} · {total} entries</span>
        <Button variant="outline" size="sm" disabled={loading || offset + AUDIT_PAGE_SIZE >= total}
          onClick={() => setOffset(offset + AUDIT_PAGE_SIZE)}>Next</Button>
      </div>
    </div>
  );
};

export default AgentAudit;
